/**
 * Centrale helper voor PDF-acties op iPhone/iPad/Desktop.
 * Lost de iOS Safari blob-URL bug op via server-side temp-URL.
 *
 * Gebruik:
 *   import { fetchPdfActions, openPdf, sharePdf, downloadPdf } from '@/lib/pdfActions';
 *   const actions = await fetchPdfActions({ endpoint: '/documenten/FAC-001/pdf', label: 'Factuur FAC-001' });
 *   await openPdf(actions);
 *   await sharePdf(actions);
 *   downloadPdf(actions);
 */
import API from '@/lib/api';
import { isIOS } from '@/lib/platform';

/**
 * Haal PDF op + bewaar zowel blob (voor share/download) als server-URL (voor iOS open).
 *
 * @param {Object} opts
 * @param {string} opts.endpoint - API path zoals '/documenten/FAC-001/pdf' (zonder ?token)
 * @param {string} [opts.label] - Mens-leesbare titel voor share-sheet (bv. 'Factuur FAC-001')
 * @param {'GET'|'POST'} [opts.method='GET']
 * @param {Object} [opts.body] - Voor POST endpoints
 * @param {Object} [opts.params] - Query string params
 * @param {string} [opts.filename] - Override filename (anders uit server-headers gehaald)
 * @returns {Promise<{ blob, url, file, fname, serverUrl, label }>}
 */
export async function fetchPdfActions({ endpoint, label = 'Document', method = 'GET', body = null, params = null, filename = null }) {
  const ios = isIOS();

  // Op iOS: stap 1 = token aanvragen (server slaat PDF op). Stap 2 = blob ophalen via
  // de SAME temp-URL — geen tweede generatie nodig, server serveert uit cache.
  // Op desktop: directe blob-fetch (één gen).
  if (ios) {
    let serverUrl = null;
    let serverFname = filename;
    try {
      let tokenRes;
      if (method === 'POST') {
        tokenRes = await API.post(endpoint, { ...(body || {}), mode: 'token' }, { params });
      } else {
        tokenRes = await API.get(endpoint, { params: { ...(params || {}), token: 1 } });
      }
      if (tokenRes.data?.token && tokenRes.data?.url) {
        serverUrl = `${process.env.REACT_APP_BACKEND_URL}${tokenRes.data.url}`;
        if (!serverFname) serverFname = tokenRes.data.fname;
      }
    } catch (e) {
      // Token-mode niet ondersteund op dit endpoint → val terug op directe blob-fetch
    }

    // Als token werkte, fetch de blob via temp-pdf (geen tweede PDF-generatie)
    if (serverUrl) {
      try {
        const blobRes = await fetch(serverUrl, { credentials: 'omit' });
        const blob = await blobRes.blob();
        const url = URL.createObjectURL(blob);
        if (!serverFname) serverFname = `${label.replace(/[^a-zA-Z0-9-_.]/g, '_')}.pdf`;
        let file = null;
        try { file = new File([blob], serverFname, { type: 'application/pdf' }); } catch { /* oude browsers */ }
        setTimeout(() => { try { URL.revokeObjectURL(url); } catch { /* noop */ } }, 5 * 60_000);
        return { blob, url, file, fname: serverFname, serverUrl, label };
      } catch (e) {
        // Blob-fetch faalde → return enkel serverUrl (open werkt nog, delen niet)
        return { blob: null, url: null, file: null, fname: serverFname || `${label}.pdf`, serverUrl, label };
      }
    }
    // iOS zonder token-support → val door naar generieke blob-flow hieronder
  }

  // Niet-iOS, of iOS waar token-mode niet werkte: directe blob-fetch (1 generatie)
  const cfg = { responseType: 'blob', params };
  const r = method === 'POST' ? await API.post(endpoint, body || {}, cfg) : await API.get(endpoint, cfg);
  const blob = r.data;
  const url = URL.createObjectURL(blob);

  // Filename fallback: extract uit Content-Disposition, anders gebruik label
  if (!filename) {
    const cd = r.headers?.['content-disposition'] || '';
    const m = cd.match(/filename\*?=(?:UTF-8'')?(?:"([^"]+)"|([^;]+))/i);
    filename = (m && (m[1] || m[2])) ? (m[1] || m[2]).trim() : `${label.replace(/[^a-zA-Z0-9-_.]/g, '_')}.pdf`;
  }
  let file = null;
  try { file = new File([blob], filename, { type: 'application/pdf' }); } catch { /* oude browsers */ }
  setTimeout(() => { try { URL.revokeObjectURL(url); } catch { /* noop */ } }, 5 * 60_000);
  return { blob, url, file, fname: filename, serverUrl: null, label };
}

/** Open PDF in viewer. Op iOS gebruikt server-URL, anders blob in nieuwe tab. */
export function openPdf(actions) {
  if (!actions) return;
  const ios = isIOS();
  const target = (ios && actions.serverUrl) ? actions.serverUrl : actions.url;
  if (!target) {
    console.error('openPdf: geen geldige URL beschikbaar');
    return;
  }
  if (ios) {
    window.location.href = target;
    return;
  }
  const w = window.open(target, '_blank');
  if (!w) window.location.href = target;
}

/** Trigger download via <a download> simulatie (cross-platform). */
export function downloadPdf(actions) {
  if (!actions) return;
  const a = document.createElement('a');
  a.href = actions.url;
  a.download = actions.fname;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/** Native share-sheet (iOS/Android) met PDF als bijlage. Fallback: kopieer link. */
export async function sharePdf(actions, { onCopyMessage = 'Link gekopieerd', onError = null } = {}) {
  if (!actions) return;
  try {
    const data = {
      title: actions.label,
      text: actions.label,
      files: actions.file ? [actions.file] : [],
    };
    if (navigator.canShare && actions.file && navigator.canShare({ files: data.files })) {
      await navigator.share(data);
      return 'shared';
    }
    if (navigator.share) {
      await navigator.share({ title: data.title, text: data.text });
      return 'shared-no-file';
    }
    // Geen Share API → kopieer URL
    try { await navigator.clipboard.writeText(actions.url); return onCopyMessage; } catch { /* noop */ }
    return null;
  } catch (e) {
    if (e?.name === 'AbortError') return 'cancelled';
    if (onError) onError(e);
    return null;
  }
}

/**
 * Deel de PDF-link direct via WhatsApp (wa.me deep-link).
 *
 * Werkt op mobiel (opent WhatsApp-app) én desktop (opent WhatsApp Web).
 * Verlengt eerst de temp-PDF-TTL naar 24u zodat de ontvanger tijd heeft om de link
 * te openen (default is 10 min, wat te kort is voor doorsturen).
 *
 * @param {Object} actions - Result van fetchPdfActions()
 * @param {Object} [opts]
 * @param {number} [opts.ttlHours=24] - Hoe lang de shared link geldig blijft (max 168 = 7d)
 * @param {string} [opts.telefoon] - Optioneel: pre-fill een specifiek nummer (E.164 zonder +, bv "32471234567")
 * @param {string} [opts.tekst] - Optioneel: custom tekst (default = label + link)
 * @returns {Promise<'shared'|'no-link'|'error'>}
 */
export async function shareViaWhatsApp(actions, { ttlHours = 24, telefoon = '', tekst = null } = {}) {
  if (!actions || !actions.serverUrl) {
    // Geen server-URL beschikbaar → we kunnen geen shareable link genereren.
    // Val terug op standaard sharePdf zodat gebruiker via native sheet WhatsApp kan kiezen.
    return 'no-link';
  }
  // Verleng TTL zodat ontvanger tijd heeft om te klikken (default: 24u)
  try {
    // Extract token uit serverUrl: .../api/temp-pdf/<token>
    const tokenMatch = actions.serverUrl.match(/\/temp-pdf\/([^/?#]+)/);
    const token = tokenMatch ? tokenMatch[1] : null;
    if (token) {
      // TTL-extend is best-effort — als het faalt (bv oude server) valt de link terug op 10 min
      await API.post(`/temp-pdf/${token}/extend`, null, { params: { hours: ttlHours } }).catch(() => null);
    }
  } catch { /* niet-fataal — link werkt sowieso ten minste 10 min */ }

  // Bouw WhatsApp deep-link. Formaat:
  //   https://wa.me/[nummer]?text=<encoded>
  // wa.me werkt op alle platforms (mobiel opent WhatsApp-app, desktop opent WhatsApp Web).
  const bericht = tekst || `${actions.label}\n${actions.serverUrl}`;
  const enc = encodeURIComponent(bericht);
  const nummer = (telefoon || '').replace(/[^\d]/g, ''); // enkel cijfers, wa.me pakt geen "+"
  const waUrl = `https://wa.me/${nummer}?text=${enc}`;
  // Nieuwe tab / app openen. Op mobiel zal iOS/Android automatisch WhatsApp-app openen.
  const w = window.open(waUrl, '_blank', 'noopener,noreferrer');
  if (!w) {
    // Popup geblokkeerd → navigeer huidige tab (op mobiel opent dit gewoon WhatsApp)
    window.location.href = waUrl;
  }
  return 'shared';
}
